import { MapPin, ShieldCheck, Users } from "lucide-react";

import type { AboutUsCopy } from "@components/bluetooth-landing/AboutUs";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { NavbarCopy } from "@components/bluetooth-landing/Navbar";

export const navbarCopyPl: NavbarCopy = {
  links: {
    targetAudience: "Dla kogo",
    values: "Specjalizacja",
    services: "Usługi",
    process: "Proces",
    about: "O nas",
    faq: "FAQ",
  },
  cta: "Omów SLA",
};

export const aboutUsCopyPl = (
  overrides?: Partial<AboutUsCopy["header"]>,
): AboutUsCopy => ({
  header: {
    h2: "O nas",
    h3: "Polski software house do utrzymania aplikacji",
    p1: "Jesteśmy software house'em z Polski — przejmujemy opiekę nad istniejącymi aplikacjami mobilnymi i webowymi: monitoring, SLA, poprawki i spokojny rozwój bez budowy od zera.",
    p2: "Mateusz pilnuje UX i jakości release'ów, Kuba odpowiada za backend, infrastrukturę i monitoring, a zespół produktowy reaguje na incydenty według ustalonego SLA — nie przez warstwę account managerów.",
    p3: "Zgodność z RODO",
    p4: "Dane w UE",
    Icon: ShieldCheck,
    imageAlt: "Zespół Blues Brackets - utrzymanie aplikacji i SLA",
    ...overrides,
  },
  values: [
    {
      Icon: Users,
      title: "Pracujesz bezpośrednio z inżynierami",
      description:
        "Osoba na rozmowie to ktoś, kto czyta logi, poprawia kod i wdraża hotfix — bez przekazywania zgłoszeń przez kilka warstw.",
    },
    {
      Icon: MapPin,
      title: "Rozumiemy systemy po innym dostawcy",
      description:
        "Stabilizacja, dokumentacja i plan utrzymania zamiast natychmiastowego „przepiszmy wszystko” — szczególnie przy legacy i niepełnym handoverze.",
    },
    {
      Icon: ShieldCheck,
      title: "Dane i infrastruktura w UE",
      description:
        "Monitoring, hosting i procesy incydentowe projektujemy z myślą o RODO i dostępności w godzinach biznesowych UE.",
    },
  ],
  cta: {
    text: "Napisz do nas",
    href: "#kontakt",
  },
});

export const contactFormPl: FooterCopy["contactForm"] = {
  heading: "Napisz do nas",
  fields: {
    name: {
      label: "Imię i nazwisko",
      placeholder: "Jan Kowalski",
      requiredError: "Podaj imię i nazwisko",
    },
    email: {
      label: "Adres e-mail",
      placeholder: "jan@firma.pl",
      requiredError: "Podaj adres e-mail",
      invalidError: "Podaj poprawny adres e-mail",
    },
    topic: {
      label: "Opis aplikacji / potrzeb SLA",
      placeholder:
        "Krótko opisz stack (mobile/web), czy macie monitoring, jak dziś reagujecie na błędy i jakiego SLA szukacie...",
      requiredError: "Opisz swój projekt",
      maxLengthError: "Opis może mieć maksymalnie 2000 znaków",
    },
  },
  submit: "Wyślij zapytanie",
  submitting: "Wysyłanie...",
  successMessage: "Wiadomość wysłana!",
  errorMessage: "Nie udało się wysłać wiadomości :(",
  responseTime: "Odpowiemy w ciągu 24 godzin w dni robocze.",
  emailSubjectPrefix: "Imię i nazwisko",
};

export const maintenanceClusterLinks = [
  {
    href: "/utrzymanie-i-audyt-oprogramowania",
    text: "Utrzymanie i audyt oprogramowania",
  },
  {
    href: "/utrzymanie-aplikacji",
    text: "Utrzymanie aplikacji",
  },
  {
    href: "/audyt-kodu-i-modernizacja-legacy",
    text: "Audyt kodu i modernizacja legacy",
  },
  {
    href: "/przejecie-projektu-od-innego-wykonawcy",
    text: "Przejęcie projektu od innego wykonawcy",
  },
  { href: "/aplikacje-mobilne-i-webowe", text: "Aplikacje mobilne i webowe" },
  { href: "/blog", text: "Blog" },
  { href: "/projects", text: "Projekty" },
] as const;

export const maintenanceKnowledgeBaseLinks = [
  {
    href: "/pl/blogs/ile-kosztuje-utrzymanie-lub-audyt-aplikacji",
    text: "Ile kosztuje utrzymanie lub audyt aplikacji?",
  },
  {
    href: "/pl/blogs/jak-bezpiecznie-zmienic-dostawce-it",
    text: "Jak bezpiecznie zmienić dostawcę IT?",
  },
] as const;

export const organizationStructuredData = (description: string) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Blues Brackets",
  url: "https://bluesbrackets.com",
  logo: "https://bluesbrackets.com/favicon-dark.png",
  description,
  areaServed: "EU",
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    email: "hello@bluesbrackets.com",
    availableLanguage: ["Polish", "English"],
  },
});

export const serviceStructuredData = (
  name: string,
  description: string,
  serviceType: string,
) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  provider: {
    "@type": "Organization",
    name: "Blues Brackets",
  },
  description,
  areaServed: "EU",
  serviceType,
});

export const faqStructuredData = (
  faqs: readonly { q: string; a: string }[],
) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(faq => ({
    "@type": "Question",
    name: faq.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.a,
    },
  })),
});
